import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import { useCompany } from "../../../contexts/CompanyContext";
import { getServiceIcon } from "../../../utils/themeUtils";
import { Icons } from "../../shared/Icons";
import { SidebarButton } from "./SidebarButton";

const CADASTRO_PATHS = ["/services", "/clients", "/professionals"] as const;

interface SidebarCadastrosGroupProps {
  isMinimized: boolean;
  isMobileMenuOpen: boolean;
  primaryColor: string;
  primaryColorHover: string;
  getBasePath: () => string;
  onNavigate: () => void;
}

export const SidebarCadastrosGroup: React.FC<SidebarCadastrosGroupProps> = ({
  isMinimized,
  isMobileMenuOpen,
  primaryColor,
  primaryColorHover,
  getBasePath,
  onNavigate,
}) => {
  const location = useLocation();
  const { company } = useCompany();
  const base = getBasePath();

  const isChildActive = CADASTRO_PATHS.some((path) => {
    const fullPath = `${base}${path}`;
    return (
      location.pathname === fullPath ||
      location.pathname.startsWith(`${fullPath}/`)
    );
  });

  const [expanded, setExpanded] = useState(() => isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setExpanded(true);
    }
  }, [isChildActive]);

  const compact = isMinimized && !isMobileMenuOpen;
  const buttonProps = {
    primaryColor,
    primaryColorHover,
    getBasePath,
    onNavigate,
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <SidebarButton
          icon={getServiceIcon(company)}
          label="Serviços"
          path="/services"
          isMinimized
          {...buttonProps}
        />
        <SidebarButton
          icon={<Icons.Group sx={{ fontSize: 20 }} />}
          label="Clientes"
          path="/clients"
          isMinimized
          {...buttonProps}
        />
        <SidebarButton
          icon={<Icons.Person sx={{ fontSize: 20 }} />}
          label="Profissionais"
          path="/professionals"
          isMinimized
          {...buttonProps}
        />
      </div>
    );
  }

  const parentActiveClass = isChildActive
    ? "bg-white text-gray-900 shadow-md dark:bg-slate-700 dark:text-white"
    : "text-gray-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800";

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base ${parentActiveClass}`}
        style={
          isChildActive
            ? { boxShadow: `inset 3px 0 0 ${primaryColor}` }
            : undefined
        }
        aria-expanded={expanded}
      >
        <span className="flex items-center justify-center [&>.MuiSvgIcon-root]:!text-[1.25rem] sm:[&>.MuiSvgIcon-root]:!text-[1.125rem]">
          <Icons.Folder sx={{ fontSize: 20 }} />
        </span>
        <span className="truncate flex-1 text-left">Cadastros</span>
        <ExpandMore
          sx={{
            fontSize: 22,
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {expanded && (
        <div className="pl-2 border-l-2 border-gray-200 dark:border-slate-600 ml-3 space-y-1">
          <SidebarButton
            icon={getServiceIcon(company, 18)}
            label="Serviços"
            path="/services"
            isMinimized={false}
            nested
            {...buttonProps}
          />
          <SidebarButton
            icon={<Icons.Group sx={{ fontSize: 18 }} />}
            label="Clientes"
            path="/clients"
            isMinimized={false}
            nested
            {...buttonProps}
          />
          <SidebarButton
            icon={<Icons.Person sx={{ fontSize: 18 }} />}
            label="Profissionais"
            path="/professionals"
            isMinimized={false}
            nested
            {...buttonProps}
          />
        </div>
      )}
    </div>
  );
};
